/**
 * Survey Storage Utility
 * Manages localStorage persistence for survey form progress
 * Data is stored in localStorage and may be treated as expired based on a TTL.
 */

import { deleteStoredSessionGroupId } from '@/components/survey-form/helper';

export interface SurveyTrackingState {
    firstStartedAt?: string;
    lastEnteredAt?: string;
    activeDurationSeconds?: number;
    visitCount?: number;
    editCount?: number;
    completionEventSentAt?: string;
    completionState?: 'initial' | 'edited';
    completedAt?: string;
    fullCompletedAt?: string; // time of the FIRST full completion (all questions incl. optional); preserved across edits
    completionSpanSeconds?: number;
    completionSpanDays?: number;
    editStartEventSentAt?: string;
    editSubmitEventSentAt?: string;
    editSessionStartedAt?: string;
}

export interface SurveyProgress {
    slug: string;
    answers: Record<string, Array<{ values: string[] }>>;
    completedStepsPerCategory: Record<string, number>;
    visitedCategories?: string[]; // Array of category IDs that have been visited/initialized
    currentCategoryId: string;
    currentStepIndex: number;
    currentMode: 'categoryIntro' | 'question' | 'result';
    lastUpdated: string;
    userId: string;
    restartGroupId?: string;
    isCompleted?: boolean; // True when user has finished the entire survey and seen specific-result page
    isCompletedWithOptional?: boolean; // True when the optional category has been fully answered
    tracking?: SurveyTrackingState;
}

export interface SurveyProgressLoadOptions {
    /**
     * If provided, data older than ttlMs will be cleared and treated as missing.
     */
    ttlMs?: number;
    /**
     * Override current time for testing.
     */
    nowMs?: number;
}

const STORAGE_PREFIX = 'survey_progress_';

const DEFAULT_TRACKING_STATE: SurveyTrackingState = {
    activeDurationSeconds: 0,
    visitCount: 0,
    editCount: 0,
    completionSpanSeconds: 0,
    completionSpanDays: 0,
};

const toFiniteNumber = (value: unknown, fallback = 0): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    return fallback;
};

const diffSeconds = (startedAt?: string, endedAt?: string): number => {
    if (!startedAt || !endedAt) {
        return 0;
    }

    const startedAtMs = Date.parse(startedAt);
    const endedAtMs = Date.parse(endedAt);
    if (!Number.isFinite(startedAtMs) || !Number.isFinite(endedAtMs)) {
        return 0;
    }

    return Math.max(0, Math.round((endedAtMs - startedAtMs) / 1000));
};

export const getSurveyTrackingState = (
    tracking?: SurveyTrackingState | null,
): SurveyTrackingState => ({
    ...DEFAULT_TRACKING_STATE,
    ...(tracking || {}),
    activeDurationSeconds: toFiniteNumber(tracking?.activeDurationSeconds),
    visitCount: toFiniteNumber(tracking?.visitCount),
    editCount: toFiniteNumber(tracking?.editCount),
    completionSpanSeconds: toFiniteNumber(tracking?.completionSpanSeconds),
    completionSpanDays: toFiniteNumber(tracking?.completionSpanDays),
});

export const startSurveyTrackingSession = (
    progress: Pick<SurveyProgress, 'tracking'>,
    nowIso = new Date().toISOString(),
): SurveyTrackingState => {
    const tracking = getSurveyTrackingState(progress.tracking);

    return {
        ...tracking,
        firstStartedAt: tracking.firstStartedAt || nowIso,
        lastEnteredAt: nowIso,
        visitCount: tracking.visitCount ? tracking.visitCount + 1 : 1,
    };
};

export const accumulateSurveyTrackingDuration = (
    progress: Pick<SurveyProgress, 'tracking'>,
    nowIso = new Date().toISOString(),
): SurveyTrackingState => {
    const tracking = getSurveyTrackingState(progress.tracking);
    const deltaSeconds = diffSeconds(tracking.lastEnteredAt, nowIso);

    return {
        ...tracking,
        activeDurationSeconds: (tracking.activeDurationSeconds || 0) + deltaSeconds,
        lastEnteredAt: undefined,
    };
};

export const markSurveyCompletionTracking = (
    progress: Pick<SurveyProgress, 'tracking'>,
    nowIso = new Date().toISOString(),
    completionState: 'initial' | 'edited' = 'initial',
): SurveyTrackingState => {
    const tracking = accumulateSurveyTrackingDuration(progress, nowIso);
    const completionSpanSeconds = diffSeconds(tracking.firstStartedAt, nowIso);

    return {
        ...tracking,
        completionEventSentAt: nowIso,
        completionState,
        completedAt: nowIso,
        completionSpanSeconds,
        completionSpanDays:
            completionSpanSeconds > 0
                ? Math.floor(completionSpanSeconds / 86400)
                : 0,
    };
};

export const markSurveyEditStartTracking = (
    progress: Pick<SurveyProgress, 'tracking'>,
    nowIso = new Date().toISOString(),
): SurveyTrackingState => {
    const tracking = getSurveyTrackingState(progress.tracking);

    return {
        ...tracking,
        editCount: (tracking.editCount || 0) + 1,
        editStartEventSentAt: nowIso,
        editSessionStartedAt: nowIso,
        lastEnteredAt: nowIso,
    };
};

export const resetSurveyTrackingState = (
    nowIso = new Date().toISOString(),
): SurveyTrackingState => ({
    ...DEFAULT_TRACKING_STATE,
    firstStartedAt: nowIso,
    lastEnteredAt: nowIso,
    visitCount: 1,
});

/**
 * Save survey progress to user profile metadata
 * @param slug - Survey slug identifier
 * @param progress - Progress data to save
 * @param userId - User ID (guest or member)
 */
export async function saveSurveyProgress(
    slug: string,
    progress: Omit<SurveyProgress, 'slug' | 'lastUpdated' | 'userId'>,
    userId: string,
): Promise<void> {
    try {
        const { saveSurveyProgressToMetadata } = await import('@/actions/user');
        const result = await saveSurveyProgressToMetadata(
            userId,
            slug,
            progress,
        );

        if (!result.success) {
            console.error(
                '❌ Failed to save survey progress to metadata:',
                result.error,
            );
        }
    } catch (error) {
        console.error('❌ Failed to save survey progress:', error);
    }
}

/**
 * Mark survey as completed
 * @param slug - Survey slug identifier
 * @param userId - User ID (guest or member)
 */
export async function markSurveyAsCompleted(
    slug: string,
    userId: string,
): Promise<void> {
    try {
        const { loadSurveyProgressFromMetadata, saveSurveyProgressToMetadata } =
            await import('@/actions/user');

        // Load current progress
        const currentProgress = await loadSurveyProgressFromMetadata(
            userId,
            slug,
        );

        if (!currentProgress) {
            console.warn('⚠️ No survey progress found to mark as completed');
            return;
        }

        const updatedProgress = {
            ...currentProgress,
            isCompleted: true,
        };

        const result = await saveSurveyProgressToMetadata(
            userId,
            slug,
            updatedProgress,
        );

        if (result.success) {
            console.log('✅ Survey marked as completed:', { slug, userId });
        } else {
            console.error(
                '❌ Failed to mark survey as completed:',
                result.error,
            );
        }
    } catch (error) {
        console.error('❌ Failed to mark survey as completed:', error);
    }
}

/**
 * Check if survey has been completed
 * @param slug - Survey slug identifier
 * @param userId - User ID (guest or member)
 * @returns true if survey was completed
 */
export async function isSurveyCompleted(
    slug: string,
    userId: string,
): Promise<boolean> {
    try {
        const { loadSurveyProgressFromMetadata } = await import(
            '@/actions/user'
        );
        const progress = await loadSurveyProgressFromMetadata(userId, slug);

        return progress?.isCompleted === true;
    } catch (error) {
        console.error('❌ Failed to check survey completion:', error);
        return false;
    }
}

/**
 * Load survey progress from user profile metadata.
 * If a TTL is provided, expired data will be cleared and treated as missing.
 * @param slug - Survey slug identifier
 * @param userId - User ID (guest or member)
 * @param options - Optional TTL and timestamp options
 */
export async function loadSurveyProgress(
    slug: string,
    userId: string,
): Promise<SurveyProgress | null>;
export async function loadSurveyProgress(
    slug: string,
    userId: string,
    options?: SurveyProgressLoadOptions,
): Promise<SurveyProgress | null>;
export async function loadSurveyProgress(
    slug: string,
    userId: string,
    options?: SurveyProgressLoadOptions,
): Promise<SurveyProgress | null> {
    try {
        const { loadSurveyProgressFromMetadata } = await import(
            '@/actions/user'
        );
        const data = await loadSurveyProgressFromMetadata(userId, slug);

        if (!data) {
            return null;
        }

        // Validate data structure
        if (
            !data.slug ||
            !data.answers ||
            !data.lastUpdated ||
            data.slug !== slug ||
            data.userId !== userId
        ) {
            console.warn('⚠️ Invalid survey progress data, clearing...', {
                expectedSlug: slug,
                actualSlug: data.slug,
                expectedUserId: userId,
                actualUserId: data.userId,
            });
            await clearSurveyProgress(slug, userId);
            return null;
        }

        // TTL validation
        const ttlMs = options?.ttlMs;
        if (typeof ttlMs === 'number' && Number.isFinite(ttlMs) && ttlMs > 0) {
            const nowMs =
                typeof options?.nowMs === 'number'
                    ? options!.nowMs!
                    : Date.now();
            const lastUpdatedMs = Date.parse(data.lastUpdated);
            if (!Number.isFinite(lastUpdatedMs)) {
                console.warn(
                    '⚠️ Invalid lastUpdated, clearing survey progress...',
                );
                await clearSurveyProgress(slug, userId);
                return null;
            }

            if (nowMs - lastUpdatedMs > ttlMs) {
                console.log('🕒 Survey progress expired by TTL, clearing...', {
                    slug,
                    userId,
                    lastUpdated: data.lastUpdated,
                    ttlMs,
                });
                await clearSurveyProgress(slug, userId);
                return null;
            }
        }

        return {
            ...data,
            tracking: getSurveyTrackingState(data.tracking),
        };
    } catch (error) {
        console.error('❌ Failed to load survey progress:', error);
        return null;
    }
}

/**
 * Clear survey progress from user profile metadata
 * @deprecated Use clearSurveyProgressAction from @/actions/user instead
 * This function only clears metadata and should not be used directly from client components
 * @param slug - Survey slug identifier
 * @param userId - User ID (guest or member)
 */
export async function clearSurveyProgress(
    slug: string,
    userId: string,
): Promise<void> {
    try {
        const { clearSurveyProgressFromMetadata } = await import(
            '@/actions/user'
        );
        const result = await clearSurveyProgressFromMetadata(userId, slug);

        if (!result.success) {
            console.error(
                '❌ Failed to clear survey progress from metadata:',
                result.error,
            );
        }
    } catch (error) {
        console.error('❌ Failed to clear survey progress:', error);
    }
}

/**
 * Check if survey has saved progress
 * @param slug - Survey slug identifier
 * @param userId - User ID (guest or member)
 * @returns true if progress exists
 */
export function hasSurveyProgress(slug: string, userId: string): boolean {
    try {
        const storageKey = `${STORAGE_PREFIX}${slug}_${userId}`;
        const stored = localStorage.getItem(storageKey);
        return !!stored;
    } catch {
        return false;
    }
}

/**
 * Get all saved survey slugs
 * @returns Array of survey slugs with saved progress
 */
export function getAllSavedSurveySlugs(): string[] {
    try {
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(STORAGE_PREFIX)) {
                keys.push(key.replace(STORAGE_PREFIX, ''));
            }
        }

        return keys;
    } catch {
        return [];
    }
}

export async function getAllStorageSurveyAnswer(
    slug: string,
    userId: string,
): Promise<SurveyProgress | undefined> {
    try {
        const storageKey = `${STORAGE_PREFIX}${slug}_${userId}`;
        const stored = localStorage.getItem(storageKey);
        const data = JSON.parse(stored as string) as SurveyProgress;
        return data;
    } catch (err) {
        console.error('Failed to get all storage survey answers:', err);
    }
}

export function clearSurveySession(slug: string): void {
    try {
        localStorage.removeItem(`survey_session_${slug}`);
        deleteStoredSessionGroupId(slug);
    } catch (err) {
        console.error(`Failed to clear survey session for slug: ${slug}`, err);
    }
}

/**
 * Migration helper: Try to find and migrate old session-based progress to userId-based storage
 * This helps users who have progress saved under the old sessionId-based keys
 * @param slug - Survey slug identifier
 * @param userId - User ID to migrate progress to
 * @returns true if old data was found and migrated
 */
export function migrateSessionProgressToUserId(
    slug: string,
    userId: string,
): boolean {
    try {
        // Try to find old session-based key (without userId suffix)
        const oldKey = `${STORAGE_PREFIX}${slug}`;
        const oldStored = localStorage.getItem(oldKey);

        if (!oldStored) {
            return false; // No old data to migrate
        }

        const oldData = JSON.parse(oldStored) as SurveyProgress;

        // Validate old data structure
        if (!oldData.slug || !oldData.answers || !oldData.lastUpdated) {
            console.warn(
                '⚠️ Invalid old survey progress data, skipping migration',
            );
            return false;
        }

        // Create new storage key with userId
        const newKey = `${STORAGE_PREFIX}${slug}_${userId}`;

        // Update data structure with userId
        const migratedData: SurveyProgress = {
            ...oldData,
            userId, // Add userId field
            lastUpdated: new Date().toISOString(), // Update timestamp
        };

        // Save to new location
        localStorage.setItem(newKey, JSON.stringify(migratedData));

        // Remove old data
        localStorage.removeItem(oldKey);

        console.log(
            '✅ Migrated survey progress from session-based to userId-based storage:',
            {
                oldKey,
                newKey,
                userId,
            },
        );

        return true;
    } catch (error) {
        console.error('❌ Failed to migrate survey progress:', error);
        return false;
    }
}
